import React from "react";
import {
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Collapse,
  Button,
  Tooltip,
  Snackbar,
  IconButton,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CloseIcon from "@material-ui/icons/Close";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { css, cx } from "@emotion/css";
import { Switch, Route, Link, useHistory, useParams, useRouteMatch, Redirect } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import SearchField from "./components/SearchField";
import Editor from "./components/Editor";
import Preview from "./components/Preview";
import ReviewContent from "./ReviewContent";
import { useKeepView, useViewState } from "utils/useViewState";

import Compare from "./Compare";
import {
  useQuestions,
  useQuestionnaires,
  useQuestionGroups,
  usePublishedQuestionnaires,
  usePublishedQuestionnaireHistory,
} from "./queries";
import type { PublishedQuestionnaireListItem, QuestionnaireListItem } from "./api";
import PublishedQuestionnaireViewer from "./components/PublishedQuestionnaireViewer";
import PublishedQuestionnairePreview from "./components/previews/PublishedQuestionnairePreview";
import type { PreviewAttrs } from "./api";
import { usePersistedPreviewAttrs } from "./previewAttrsStorage";
import type { EntityType, SelectionType, Selection, PickType, PickMode, PickedItem, Toast } from "./uiTypes";
import { Area } from "../../types";
import { Colors } from "../../theme";
import HealthFormsTour from "./components/HealthFormsTour";
import { DeployedRevisions, RevisionRolloutBadges } from "./components/RolloutBadges";

const ITEM_KEYS = ["page", "highlight"];
const usePreviewPage = () => {
  const [{ page }, set] = useViewState({ page: 1 });
  return [Math.max(page, 1) - 1, (index: number) => set({ page: index + 1 })] as const;
};

type ExpandedPane = "editor" | "preview" | null;

type TabValue = EntityType | "review" | "published";

const tabToPath: Record<TabValue, string> = {
  questionnaires: "/health-forms/questionnaires",
  questionGroups: "/health-forms/question-groups",
  questions: "/health-forms/questions",
  review: "/health-forms/review",
  published: "/health-forms/published",
};

const pathToTab: Record<string, TabValue> = {
  questionnaires: "questionnaires",
  "question-groups": "questionGroups",
  questions: "questions",
  review: "review",
  published: "published",
};

const unsavedKey = (type: SelectionType, id: number) => `${type}:${id}`;

const tabToSelectionType: Record<EntityType, SelectionType> = {
  questionnaires: "questionnaire",
  questionGroups: "questionGroup",
  questions: "question",
};

const PublishedPanels: React.VFC<{
  publishedId: number | null;
  items: PublishedQuestionnaireListItem[] | undefined;
  isLoading: boolean;
  error: unknown;
  previewAttrs: PreviewAttrs;
  onAttrsChange: (attrs: PreviewAttrs) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onCommitSearch: (value?: string) => void;
  hasFilters: boolean;
  onClearAll: () => void;
}> = ({
  publishedId,
  items,
  isLoading,
  error,
  previewAttrs,
  onAttrsChange,
  searchTerm,
  onSearchTermChange,
  onCommitSearch,
  hasFilters,
  onClearAll,
}) => {
  const [expandedName, setExpandedName] = React.useState<string | null>(null);
  const [pageIndex, setPageIndex] = usePreviewPage();
  const keep = useKeepView(ITEM_KEYS);
  const historyQuery = usePublishedQuestionnaireHistory(expandedName);

  return (
    <div className={styles.panels}>
      <Paper className={cx(styles.sidebar)}>
        <Box className={styles.publishedSearch}>
          <SearchField
            value={searchTerm}
            onChange={onSearchTermChange}
            onCommit={onCommitSearch}
            placeholder="Search published..."
          />
          <Box className={styles.countRow}>
            <Button size="small" className={styles.clearAllButton} onClick={onClearAll} disabled={!hasFilters}>
              Clear all
            </Button>
            <Typography variant="caption" color="textSecondary">
              {items?.length ?? 0} match{(items?.length ?? 0) !== 1 ? "es" : ""}
            </Typography>
          </Box>
        </Box>
        {isLoading ? (
          <Box p={2} display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" style={{ padding: 12 }}>
            Failed to load
          </Typography>
        ) : (
          <List dense className={styles.publishedList}>
            {items?.map(p => (
              <React.Fragment key={p.id}>
                <ListItem
                  button
                  component={Link}
                  to={keep(`${tabToPath.published}/${p.id}`)}
                  selected={publishedId === p.id}
                  style={p.is_active ? undefined : { backgroundColor: Colors.WARNING_BG }}
                  onClick={() => setExpandedName(p.name)}
                >
                  <ListItemText
                    primary={p.name}
                    secondary={<DeployedRevisions rollout={p.rollout} publishedAt={p.published_at} />}
                    primaryTypographyProps={p.is_active ? undefined : { style: { color: Colors.WARNING_TEXT } }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label={expandedName === p.name ? "Hide revisions" : "Show revisions"}
                      onClick={() => setExpandedName(expandedName === p.name ? null : p.name)}
                    >
                      {expandedName === p.name ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Collapse in={expandedName === p.name && !!historyQuery.data && historyQuery.data.length > 0}>
                  <List dense disablePadding className={styles.historyList}>
                    {historyQuery.data?.map(h => (
                      <ListItem
                        key={h.id}
                        button
                        component={Link}
                        to={keep(`${tabToPath.published}/${h.id}`)}
                        selected={publishedId === h.id}
                      >
                        <ListItemText
                          primary={
                            <>
                              {new Date(h.published_at).toLocaleString()}
                              <RevisionRolloutBadges rollout={h.rollout} />
                            </>
                          }
                          secondary={h.published_by}
                          primaryTypographyProps={{ variant: "body2" }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Paper className={styles.editor}>
        {publishedId ? (
          <PublishedQuestionnaireViewer id={publishedId} />
        ) : (
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              Select a published questionnaire
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper className={styles.preview}>
        {publishedId ? (
          <PublishedQuestionnairePreview
            id={publishedId}
            attrs={previewAttrs}
            onAttrsChange={onAttrsChange}
            currentPageIndex={pageIndex}
            onPageIndexChange={setPageIndex}
          />
        ) : (
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              Select a published questionnaire to preview
            </Typography>
          </Box>
        )}
      </Paper>
    </div>
  );
};

const ReviewPanels: React.VFC<{
  items: QuestionnaireListItem[];
  selectedId: number | null;
  isLoading: boolean;
  error: unknown;
  onPublishResult: (toast: Toast) => void;
  keep: (path: string) => string;
}> = ({ items, selectedId, isLoading, error, onPublishResult, keep }) => (
  <div className={styles.panels}>
    <Paper className={styles.sidebar}>
      <Box className={styles.listHeader}>
        <Typography variant="caption" color="textSecondary">
          {items.length} to publish
        </Typography>
      </Box>
      {isLoading ? (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Typography color="error" style={{ padding: 12 }}>
          Failed to load
        </Typography>
      ) : items.length === 0 ? (
        <Box p={2}>
          <Typography variant="body2" color="textSecondary">
            Nothing to publish — all questionnaires are up to date.
          </Typography>
        </Box>
      ) : (
        <List dense className={styles.publishedList}>
          {items.map(q => (
            <ListItem
              button
              key={q.id}
              component={Link}
              to={keep(`${tabToPath.review}/${q.id}`)}
              selected={selectedId === q.id}
            >
              <ListItemText primary={q.name || `Questionnaire #${q.id}`} />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>

    <Paper className={styles.reviewPane}>
      {selectedId ? (
        <ReviewContent id={selectedId} onPublishResult={onPublishResult} />
      ) : (
        <Box p={2}>
          <Typography variant="body2" color="textSecondary">
            Select a questionnaire to review
          </Typography>
        </Box>
      )}
    </Paper>
  </div>
);

const HealthFormsContent: React.VFC = () => {
  const history = useHistory();
  const params = useParams<{ tab?: string; id?: string }>();
  const [tourRun, setTourRun] = React.useState(false);
  const [expandedPane, setExpandedPane] = React.useState<ExpandedPane>(null);
  const [selectionByTab, setSelectionByTab] = React.useState<Record<EntityType, number | null>>({
    questionnaires: null,
    questionGroups: null,
    questions: null,
  });
  const [publishedSelectionId, setPublishedSelectionId] = React.useState<number | null>(null);
  const [reviewSelectionId, setReviewSelectionId] = React.useState<number | null>(null);
  const [toast, setToast] = React.useState<Toast | null>(null);
  const [unsavedKeys, setUnsavedKeys] = React.useState<ReadonlySet<string>>(new Set());
  const handleUnsavedChange = React.useCallback((type: SelectionType, id: number, dirty: boolean) => {
    const key = unsavedKey(type, id);
    setUnsavedKeys(prev => {
      if (dirty === prev.has(key)) return prev;
      const next = new Set(prev);
      if (dirty) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  const isUnsaved = React.useCallback(
    (type: SelectionType, id: number) => unsavedKeys.has(unsavedKey(type, id)),
    [unsavedKeys]
  );

  React.useEffect(() => {
    if (unsavedKeys.size === 0) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsavedKeys]);
  const [{ published_search: publishedCommittedSearch, highlight }, setView] = useViewState({
    published_search: "",
    highlight: "",
  });
  const [publishedSearchTerm, setPublishedSearchTerm] = React.useState(publishedCommittedSearch);
  const commitPublishedSearch = (override?: string) => setView({ published_search: override ?? publishedSearchTerm });
  const hasPublishedFilters = !!(publishedSearchTerm || publishedCommittedSearch);
  const clearPublishedFilters = () => {
    setPublishedSearchTerm("");
    setView({ published_search: "" });
  };
  const publishedFilters = React.useMemo(
    () => (publishedCommittedSearch ? { search: publishedCommittedSearch } : undefined),
    [publishedCommittedSearch]
  );
  const [previewAttrs, setPreviewAttrs] = usePersistedPreviewAttrs();

  const [currentPageIndex, setCurrentPageIndex] = usePreviewPage();
  const keep = useKeepView(ITEM_KEYS);
  const onHighlightConsumed = React.useCallback(() => setView({ highlight: "" }), [setView]);

  const [pickMode, setPickMode] = React.useState<PickMode>(null);
  const [pickedItem, setPickedItem] = React.useState<PickedItem>(null);

  const questionsQuery = useQuestions();
  const questionnairesQuery = useQuestionnaires();
  const questionGroupsQuery = useQuestionGroups();
  const publishedQuery = usePublishedQuestionnaires(publishedFilters);

  const tab: TabValue = params.tab ? pathToTab[params.tab] : "questionnaires";
  const entityTab: EntityType | null = tab === "published" || tab === "review" ? null : tab;

  const unpublishedItems = (questionnairesQuery.data ?? []).filter(q => q.has_unpublished_changes);

  const firstIdByTab: Record<EntityType, number | undefined> = {
    questionnaires: questionnairesQuery.data?.[0]?.id,
    questionGroups: questionGroupsQuery.data?.[0]?.id,
    questions: questionsQuery.data?.[0]?.id,
  };

  const currentId = entityTab
    ? params.id
      ? parseInt(params.id, 10)
      : (selectionByTab[entityTab] ?? firstIdByTab[entityTab] ?? null)
    : null;
  const selection: Selection = currentId && entityTab ? { type: tabToSelectionType[entityTab], id: currentId } : null;

  const publishedId =
    tab === "published"
      ? params.id
        ? parseInt(params.id, 10)
        : (publishedSelectionId ?? publishedQuery.data?.[0]?.id ?? null)
      : null;

  const rememberedReviewId =
    reviewSelectionId && unpublishedItems.some(q => q.id === reviewSelectionId) ? reviewSelectionId : null;
  const reviewId =
    tab === "review"
      ? params.id
        ? parseInt(params.id, 10)
        : (rememberedReviewId ?? unpublishedItems[0]?.id ?? null)
      : null;

  React.useEffect(() => {
    const paramId = params.id ? parseInt(params.id, 10) : null;
    if (paramId && entityTab && paramId !== selectionByTab[entityTab]) {
      setSelectionByTab(prev => ({ ...prev, [entityTab]: paramId }));
    }
    if (paramId && tab === "published" && paramId !== publishedSelectionId) {
      setPublishedSelectionId(paramId);
    }
    if (paramId && tab === "review" && paramId !== reviewSelectionId) {
      setReviewSelectionId(paramId);
    }
  }, [params.id, entityTab, selectionByTab, tab, publishedSelectionId, reviewSelectionId]);

  React.useEffect(() => {
    setPickMode(null);
  }, [selection?.id]);

  React.useEffect(() => {
    if (!params.id && currentId && entityTab) {
      history.replace(keep(`${tabToPath[entityTab]}/${currentId}`));
    }
    if (!params.id && publishedId && tab === "published") {
      history.replace(keep(`${tabToPath.published}/${publishedId}`));
    }
    if (!params.id && reviewId && tab === "review") {
      history.replace(keep(`${tabToPath.review}/${reviewId}`));
    }
  }, [params.id, currentId, entityTab, publishedId, reviewId, tab, history, keep]);

  const tabHref = (value: TabValue): string => {
    if (value === "review") {
      return rememberedReviewId ? `${tabToPath.review}/${rememberedReviewId}` : tabToPath.review;
    }
    if (value === "published") {
      return publishedSelectionId ? `${tabToPath.published}/${publishedSelectionId}` : tabToPath.published;
    }
    const savedId = selectionByTab[value];
    return savedId ? `${tabToPath[value]}/${savedId}` : tabToPath[value];
  };

  const tabs: { value: TabValue; label: React.ReactNode; tour: string }[] = [
    {
      value: "questionnaires",
      label: `Questionnaires (${questionnairesQuery.data?.length ?? "..."})`,
      tour: "hf-tab-questionnaires",
    },
    {
      value: "questionGroups",
      label: `Question Groups (${questionGroupsQuery.data?.length ?? "..."})`,
      tour: "hf-tab-questionGroups",
    },
    { value: "questions", label: `Questions (${questionsQuery.data?.length ?? "..."})`, tour: "hf-tab-questions" },
    {
      value: "review",
      label: `Review (${questionnairesQuery.data ? unpublishedItems.length : "..."})`,
      tour: "hf-tab-review",
    },
    { value: "published", label: `Published (${publishedQuery.data?.length ?? "..."})`, tour: "hf-tab-published" },
  ];

  const handleCreated = (sel: Selection) => {
    if (sel) {
      const selTab =
        sel.type === "question" ? "questions" : sel.type === "questionnaire" ? "questionnaires" : "questionGroups";
      history.push(keep(`${tabToPath[selTab]}/${sel.id}`));
    }
  };

  const toggleExpand = (pane: "editor" | "preview") => {
    setExpandedPane(expandedPane === pane ? null : pane);
  };

  const handleRequestPick = (pickType: PickType) => {
    setPickMode({ pickType });
    setPickedItem(null);
  };

  const handlePick = (id: number) => {
    if (!pickMode) return;
    setPickedItem({ pickType: pickMode.pickType, id });
    setPickMode(null);
  };

  const handleCancelPick = () => {
    setPickMode(null);
  };

  const handlePickConsumed = React.useCallback(() => {
    setPickedItem(null);
  }, []);

  return (
    <div className={styles.container}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        className={cx(styles.tabBar, pickMode && styles.dimmed)}
      >
        <Tabs data-tour="hf-tabs" value={tab} indicatorColor="primary">
          {tabs.map(t => (
            <Tab
              key={t.value}
              value={t.value}
              label={t.label}
              component={Link}
              to={keep(tabHref(t.value))}
              data-tour={t.tour}
            />
          ))}
        </Tabs>
        <Box display="flex" alignItems="center">
          <Tooltip title="Start the guided tour">
            <Button
              size="small"
              startIcon={<HelpOutlineIcon />}
              onClick={() => setTourRun(true)}
              className={styles.tourButton}
            >
              Tutorial
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {tab === "review" ? (
        <ReviewPanels
          items={unpublishedItems}
          selectedId={reviewId}
          isLoading={questionnairesQuery.isLoading}
          error={questionnairesQuery.error}
          onPublishResult={setToast}
          keep={keep}
        />
      ) : tab === "published" ? (
        <PublishedPanels
          publishedId={publishedId}
          items={publishedQuery.data}
          isLoading={publishedQuery.isLoading}
          error={publishedQuery.error}
          previewAttrs={previewAttrs}
          onAttrsChange={setPreviewAttrs}
          searchTerm={publishedSearchTerm}
          onSearchTermChange={setPublishedSearchTerm}
          onCommitSearch={commitPublishedSearch}
          hasFilters={hasPublishedFilters}
          onClearAll={clearPublishedFilters}
        />
      ) : (
        <div className={styles.panels}>
          {pickMode && <div className={styles.pickOverlay} onClick={handleCancelPick} />}
          <Paper
            className={cx(styles.sidebar, expandedPane && styles.hidden)}
            style={pickMode ? { position: "relative", zIndex: 2 } : undefined}
          >
            <Sidebar
              selection={selection}
              hrefFor={sel => keep(`${tabToPath[tab]}/${sel.id}`)}
              onCreated={handleCreated}
              tab={tab}
              pickMode={pickMode}
              onPick={handlePick}
              onCancelPick={handleCancelPick}
              unsavedKeys={unsavedKeys}
            />
          </Paper>

          <Paper
            className={cx(
              styles.editor,
              expandedPane === "editor" && styles.expanded,
              expandedPane === "preview" && styles.hidden
            )}
          >
            <Editor
              selection={selection}
              isExpanded={expandedPane === "editor"}
              onToggleExpand={() => toggleExpand("editor")}
              currentPageIndex={currentPageIndex}
              onPageIndexChange={setCurrentPageIndex}
              onRequestPick={handleRequestPick}
              pickedItem={pickedItem}
              onPickConsumed={handlePickConsumed}
              isPicking={!!pickMode}
              highlight={highlight || null}
              onHighlightConsumed={onHighlightConsumed}
              onUnsavedChange={handleUnsavedChange}
              isUnsaved={isUnsaved}
              onDeleted={() => {
                if (entityTab) {
                  setSelectionByTab(prev => ({ ...prev, [entityTab]: null }));
                  const firstId = firstIdByTab[entityTab];
                  if (firstId) {
                    history.push(keep(`${tabToPath[entityTab]}/${firstId}`));
                  } else {
                    history.push(keep(tabToPath[entityTab]));
                  }
                }
              }}
            />
          </Paper>

          <Paper
            data-tour="hf-preview"
            className={cx(
              styles.preview,
              expandedPane === "preview" && styles.expanded,
              expandedPane === "editor" && styles.hidden,
              pickMode && styles.dimmed
            )}
          >
            <Preview
              selection={selection}
              isExpanded={expandedPane === "preview"}
              onToggleExpand={() => toggleExpand("preview")}
              currentPageIndex={currentPageIndex}
              onPageIndexChange={setCurrentPageIndex}
              attrs={previewAttrs}
              onAttrsChange={setPreviewAttrs}
            />
          </Paper>
        </div>
      )}

      <HealthFormsTour
        run={tourRun}
        onFinish={() => setTourRun(false)}
        questionnaireId={firstIdByTab.questionnaires ?? null}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.to ? 10000 : 4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            action={
              toast.to ? (
                <>
                  <Button size="small" component={Link} to={toast.to} onClick={() => setToast(null)}>
                    View
                  </Button>
                  <IconButton size="small" color="inherit" onClick={() => setToast(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : undefined
            }
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </div>
  );
};

const TAB_PATTERN = "questionnaires|question-groups|questions|review|published";

const HealthForms: React.VFC = () => {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route exact path={path}>
        <Redirect to={Area.HealthForms_Questionnaires} />
      </Route>
      <Route exact path={`${path}/questionnaires/:name/compare`}>
        <Compare />
      </Route>
      <Route exact path={`${path}/:tab(${TAB_PATTERN})`}>
        <HealthFormsContent />
      </Route>
      <Route path={`${path}/:tab(${TAB_PATTERN})/:id`}>
        <HealthFormsContent />
      </Route>
      <Route>
        <Redirect to={Area.HealthForms_Questionnaires} />
      </Route>
    </Switch>
  );
};

export default HealthForms;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 90px);
    padding: 16px;
    box-sizing: border-box;
  `,
  tabBar: css`
    margin-bottom: 16px;
    border-bottom: 1px solid #e0e0e0;
  `,
  tourButton: css`
    white-space: nowrap;
    & .MuiButton-label {
      text-transform: none;
    }
  `,
  panels: css`
    display: flex;
    flex: 1;
    gap: 16px;
    min-height: 0;
    position: relative;
  `,
  sidebar: css`
    flex: 1;
    min-width: 250px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `,
  editor: css`
    flex: 3;
    min-width: 0;
    overflow: hidden;
    position: relative;
  `,
  preview: css`
    flex: 3;
    min-width: 0;
    overflow: hidden;
    position: relative;
  `,
  expanded: css`
    flex: 1;
    width: auto;
  `,
  hidden: css`
    display: none;
  `,
  publishedSearch: css`
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  countRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  `,
  clearAllButton: css`
    padding: 0;
    min-width: 0;
    & .MuiButton-label {
      text-transform: none;
      font-size: 0.75rem;
    }
  `,
  publishedList: css`
    padding: 0;
    flex: 1;
    overflow-y: auto;
  `,
  listHeader: css`
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
  `,
  reviewPane: css`
    flex: 6;
    min-width: 0;
    overflow: hidden;
    position: relative;
  `,
  historyList: css`
    margin-left: 16px;
    border-left: 3px solid #bdbdbd;
    background: #f5f5f5;
    & .MuiListItemText-primary {
      font-size: 0.8rem;
    }
    & .MuiListItemText-secondary {
      font-size: 0.7rem;
    }
  `,
  dimmed: css`
    opacity: 0.3;
    pointer-events: none;
  `,
  pickOverlay: css`
    position: absolute;
    inset: 0;
    z-index: 1;
    cursor: default;
  `,
};
